export interface LoadedImage {
  source: TexImageSource;
  width: number;
  height: number;
  name: string;
  release: () => void;
}

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/gif,image/bmp,image/avif,image/svg+xml';

export const IMAGE_ACCEPT = ACCEPTED_TYPES;

/**
 * Decodes a file into something `texImage2D` accepts. `createImageBitmap` is the
 * fast path; SVG and a few exotic formats only work through an `<img>` element,
 * hence the fallback.
 */
export async function loadImage(file: Blob, fallbackName = 'Image'): Promise<LoadedImage> {
  const name = file instanceof File ? file.name.replace(/\.[^.]+$/, '') : fallbackName;

  try {
    const bitmap = await createImageBitmap(file, { colorSpaceConversion: 'default' });
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      name,
      release: () => bitmap.close(),
    };
  } catch {
    return await loadViaImageElement(file, name);
  }
}

function loadViaImageElement(file: Blob, name: string): Promise<LoadedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'sync';
    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not determine the image size'));
        return;
      }
      resolve({
        source: image,
        width,
        height,
        name,
        release: () => URL.revokeObjectURL(url),
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The browser does not support this file format'));
    };
    image.src = url;
  });
}

/** Extra formats File > Open understands beyond plain images. */
export const BRUSH_ACCEPT = '.abr';

export function pickImageFiles(multiple = false): Promise<File[]> {
  return pickFiles(ACCEPTED_TYPES, multiple);
}

/** Opens the system file picker and resolves with the chosen files. */
export function pickFiles(accept: string, multiple = false): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;
    input.onchange = () => resolve(input.files ? Array.from(input.files) : []);
    // A cancelled picker fires no event in some browsers; `oncancel` covers it.
    input.oncancel = () => resolve([]);
    input.click();
  });
}

export function imageBlobFromDataTransfer(data: DataTransfer | null): File[] {
  if (!data) return [];
  return Array.from(data.files).filter((file) => file.type.startsWith('image/'));
}

function isOpenableDropName(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    lower.endsWith('.abr') ||
    lower.endsWith('.psd') ||
    lower.endsWith('.psb') ||
    lower.endsWith('.pdf') ||
    lower.endsWith('.asl') ||
    lower.endsWith('.json')
  );
}

/** Files the editor can do something with: images, PSD/PDF, styles, brush sets. */
export function droppedFiles(data: DataTransfer | null): File[] {
  if (!data) return [];
  return Array.from(data.files).filter(
    (file) =>
      file.type.startsWith('image/') ||
      file.type === 'application/pdf' ||
      isOpenableDropName(file.name),
  );
}

export async function imageBlobFromClipboard(items: ClipboardItems): Promise<Blob | null> {
  for (const item of items) {
    const type = item.types.find((t) => t.startsWith('image/'));
    if (type) return await item.getType(type);
  }
  return null;
}

type ClipboardItems = readonly ClipboardItem[];
